import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { AppInsightsService } from '../appInsights/appInsights.service';
import { SpinnerService } from '../spinner/spinner.service';

interface ActivePdfReport {
  reportName: string;
  pdfEnabled: boolean;
}

/** Vertical extent (in canvas pixels) of an element that should not be split across a page break. */
interface PdfBreakInterval {
  top: number;
  bottom: number;
}

/** The rows to keep vs. drop when exporting only the table's visible scroll window. */
interface PdfRowSelection {
  /** Live-DOM <tr> rows NOT in the table's scroll window; hidden + skipped during capture. */
  rowsToIgnore: Set<Element>;
  /** Rows in the largest data table (drives the "of M" in the note). */
  totalRows: number;
  /** Rows kept (visible in the window) for the largest table. */
  shownRows: number;
}

@Injectable({ providedIn: 'root' })
export class PdfExportService {
  private static readonly pdfOverrideStyleId = 'pdf-export-style-overrides';
  // The app page sits on this grey; white billboard tiles read against it.
  private static readonly pageBackgroundColor = '#ecedef';
  // Largest single-canvas dimension we'll request. Exceeding the browser's max canvas size yields a
  // blank (grey) bitmap, so render scale is capped to keep width*scale and height*scale under this.
  // Chrome/Firefox allow ~32,767; raise for sharper tall-report output, lower for Safari/iOS safety.
  private static readonly maxCanvasDimensionPx = 32767;
  // Defensive ceiling on rows captured per table (and the fallback count if the visible window
  // can't be resolved). The visible window is normally ~10 rows, so this rarely binds.
  private static readonly maxTableRowsInPdf = 100;
  // The data table's data rows (excludes header/footer rows and the loading-skeleton table).
  private static readonly dataRowSelector = 'tr.mat-mdc-row:not(.skeleton-row)';
  // The data table's scroll container (max-height:500px; overflow:auto). Its rect defines the
  // on-screen "window" of rows. See src/scss/_table.scss (.sd-infinite-scroll-xy).
  private static readonly scrollContainerSelector = '.sd-infinite-scroll-xy';

  private hostElementSubject = new BehaviorSubject<HTMLElement | null>(null);
  private activeReportSubject = new BehaviorSubject<ActivePdfReport | null>(null);

  pdfAvailable$ = combineLatest([
    this.hostElementSubject,
    this.activeReportSubject
  ]).pipe(
    map(([hostElement, activeReport]) => !!hostElement && activeReport?.pdfEnabled === true)
  );

  constructor(
    private appInsights: AppInsightsService,
    private spinnerService: SpinnerService
  ) {}

  registerHost(element: HTMLElement): void {
    this.hostElementSubject.next(element);
  }

  unregisterHost(element: HTMLElement): void {
    if (this.hostElementSubject.value === element) {
      this.hostElementSubject.next(null);
    }
  }

  activate(reportName: string, pdfEnabled: boolean): void {
    this.activeReportSubject.next({ reportName, pdfEnabled });
  }

  deactivate(reportName?: string): void {
    const activeReport = this.activeReportSubject.value;

    if (!activeReport || (reportName && activeReport.reportName !== reportName)) {
      return;
    }

    this.activeReportSubject.next(null);
  }

  exportPdf(): void {
    const hostElement = this.hostElementSubject.value;
    const activeReport = this.activeReportSubject.value;

    if (!hostElement || activeReport?.pdfEnabled !== true) {
      return;
    }

    this.appInsights.trackReportExported({
      ExportFormat: 'PDF',
      ReportKind: 'PrebuiltReport',
      ReportType: 'Prebuilt'
    });

    this.spinnerService.show();
    setTimeout(() => {
      void this.generatePdf(hostElement, activeReport.reportName || 'Report');
    }, 200);
  }

  private async generatePdf(exportTarget: HTMLElement, fileName: string): Promise<void> {
    // Determine the table's visible scroll-window rows BEFORE any layout mutation — preparePdfLayout
    // expands the scroll boxes (overflow:visible/height:auto) and destroys the window.
    const selection = this.computeVisibleRowSelection(exportTarget);

    const restoreStyles = this.preparePdfLayout(exportTarget);
    const hiddenRows: HTMLElement[] = [];

    try {
      await this.waitForPdfStyles();

      // Capture the full expanded layout height BEFORE hiding rows. html2canvas lays the clone out in a
      // viewport of this height; if it shrinks to just the kept rows, glyph backgrounds (e.g. the KPI
      // trend chevrons) render misaligned. The layout viewport stays at full height; the output is
      // cropped separately to the kept rows in renderPdfCanvas.
      const layoutViewportHeight = Math.ceil(exportTarget.scrollHeight || exportTarget.getBoundingClientRect().height || exportTarget.clientHeight);

      // Hide the non-visible rows at the source so the table is its true (windowed) height before
      // html2canvas captures it (html2canvas bakes the source height; trimming only in the clone makes
      // the kept rows stretch). ignoreElements then skips cloning them so the export stays fast.
      // Restored in `finally`, so the on-screen table is unchanged after export.
      selection.rowsToIgnore.forEach(row => {
        const element = row as HTMLElement;
        element.style.setProperty('display', 'none', 'important');
        hiddenRows.push(element);
      });

      const scale = this.renderScale(exportTarget);
      const canvas = await this.renderPdfCanvas(exportTarget, scale, selection, layoutViewportHeight);
      const breakIntervals = this.collectBreakIntervals(exportTarget, scale);
      const footerNote = PdfExportService.visibleRowsNote(selection.shownRows, selection.totalRows);
      const pdf = this.buildPdfDocument(canvas, breakIntervals, footerNote);

      pdf.save(`${fileName || 'Report'}.pdf`);
    } catch (error) {
      this.appInsights.trackException(this.asError(error));
    } finally {
      hiddenRows.forEach(element => element.style.removeProperty('display'));
      restoreStyles();
      this.spinnerService.hide();
    }
  }

  private preparePdfLayout(exportTarget: HTMLElement): () => void {
    document.body.classList.add('pdf-export-mode');
    exportTarget.classList.add('pdf-export-target');

    const elements = [
      exportTarget,
      document.getElementById('printdiv'),
      document.getElementById('main-content'),
      document.getElementById('stuff-to-print'),
      exportTarget.parentElement
    ].filter((element): element is HTMLElement => !!element);

    const previousStyles = new Map<HTMLElement, string | null>();

    const registerElement = (element: HTMLElement) => {
      if (!previousStyles.has(element)) {
        previousStyles.set(element, element.getAttribute('style'));
      }
    };

    // 'important' so these beat !important stylesheet caps (e.g. .transaction-report sets
    // max-height: calc(...) !important, which a plain inline style can't override — leaving the
    // box clamped with visible overflow, so its content overlaps what's below it). Reverted by
    // the style-restore returned below.
    elements.forEach(element => {
      registerElement(element);
      element.style.setProperty('height', 'auto', 'important');
      element.style.setProperty('min-height', 'auto', 'important');
      element.style.setProperty('max-height', 'none', 'important');
      element.style.setProperty('flex', 'none', 'important');
      element.style.setProperty('overflow-x', 'hidden', 'important');
      element.style.setProperty('overflow-y', 'visible', 'important');
    });

    const allElements = Array.from(exportTarget.querySelectorAll<HTMLElement>('*'));

    // Phase 1 — READ ONLY. Measure every element up front so the browser computes layout once.
    // Interleaving these layout reads (scrollHeight/getComputedStyle) with the style writes below
    // forces a synchronous reflow per element (layout thrashing) — the cause of the multi-minute prep.
    const elementsToAdjust: HTMLElement[] = [];
    for (const element of allElements) {
      const computedStyle = window.getComputedStyle(element);
      const hasVerticalClipping = element.scrollHeight > element.clientHeight + 1;
      const constrainsHeight = computedStyle.maxHeight !== 'none'
        || ['auto', 'scroll', 'hidden'].includes(computedStyle.overflowY)
        || ['auto', 'scroll', 'hidden'].includes(computedStyle.overflow);

      if (hasVerticalClipping || constrainsHeight) {
        elementsToAdjust.push(element);
      }
    }

    // Phase 2 — WRITE ONLY. These mutations dirty layout but never read it back, so they queue a
    // single reflow at the next read/paint instead of one per element.
    for (const element of elementsToAdjust) {
      registerElement(element);
      // 'important' so these beat !important stylesheet caps (see the note on the loop above).
      element.style.setProperty('height', 'auto', 'important');
      element.style.setProperty('max-height', 'none', 'important');
      element.style.setProperty('overflow-x', 'hidden', 'important');
      element.style.setProperty('overflow-y', 'visible', 'important');
    }

    // .text-panel carries margin-top: -40px (a spacing offset for the live layout). Once the export
    // expands the report, that negative margin drags text-panel footnotes (e.g. the timezone
    // disclaimer) up into the panel above them. Zero it on the live DOM — margin is a layout property,
    // so it must be corrected before html2canvas measures (the onclone paint stylesheet can't reflow
    // it). Reverted by the style-restore below.
    exportTarget.querySelectorAll<HTMLElement>('.text-panel').forEach(element => {
      registerElement(element);
      element.style.setProperty('margin-top', '0', 'important');
    });

    exportTarget.style.setProperty('background-color', PdfExportService.pageBackgroundColor);

    return () => {
      document.body.classList.remove('pdf-export-mode');
      exportTarget.classList.remove('pdf-export-target');

      previousStyles.forEach((style, element) => {
        if (style === null) {
          element.removeAttribute('style');
        } else {
          element.setAttribute('style', style);
        }
      });
    };
  }

  // Select the rows currently visible in each table's scroll window. MUST run BEFORE preparePdfLayout,
  // which expands the scroll boxes (overflow:visible/height:auto) and destroys the window. A row is
  // visible if its rect vertically overlaps its scroll box's rect — true even if the box is partly or
  // fully off the browser viewport, because getBoundingClientRect returns viewport coords for off-screen
  // elements. Non-visible rows go into rowsToIgnore (hidden + skipped at capture time, restored after).
  private computeVisibleRowSelection(exportTarget: HTMLElement): PdfRowSelection {
    const maxRows = PdfExportService.maxTableRowsInPdf;
    const rowsToIgnore = new Set<Element>();
    let totalRows = 0;
    let shownRows = 0;

    exportTarget.querySelectorAll<HTMLElement>(PdfExportService.scrollContainerSelector).forEach(box => {
      const rows = Array.from(box.querySelectorAll<HTMLElement>(PdfExportService.dataRowSelector));
      if (rows.length === 0) {
        return;
      }
      totalRows = Math.max(totalRows, rows.length);

      const boxRect = box.getBoundingClientRect();
      const kept: HTMLElement[] = [];
      for (const row of rows) {
        const rect = row.getBoundingClientRect();
        const overlapsWindow = rect.bottom > boxRect.top && rect.top < boxRect.bottom;
        if (overlapsWindow && kept.length < maxRows) {
          kept.push(row);
        } else {
          rowsToIgnore.add(row);
        }
      }

      // Safety net: if the window resolved to nothing (degenerate box rect, detached node, etc.),
      // keep the first maxRows so the export never produces an empty table.
      if (kept.length === 0) {
        const fallbackCount = Math.min(maxRows, rows.length);
        for (let i = 0; i < fallbackCount; i += 1) {
          rowsToIgnore.delete(rows[i]);
          kept.push(rows[i]);
        }
      }

      shownRows = Math.max(shownRows, kept.length);
    });

    return { rowsToIgnore, totalRows, shownRows };
  }

  // The scale html2canvas renders at; also the DOM-px -> canvas-px factor for break math. Capped so
  // the resulting canvas (content size * scale) stays under the max canvas dimension; otherwise a tall
  // report produces an oversized canvas that renders blank (grey). scrollHeight already reflects the
  // trimmed table because the non-visible rows are hidden before this runs.
  private renderScale(exportTarget: HTMLElement): number {
    const desired = Math.min(3, Math.max(2, window.devicePixelRatio || 1));

    const width = Math.ceil(exportTarget.clientWidth || exportTarget.getBoundingClientRect().width || exportTarget.scrollWidth);
    const height = Math.ceil(exportTarget.scrollHeight || exportTarget.getBoundingClientRect().height || exportTarget.clientHeight);
    const maxDim = PdfExportService.maxCanvasDimensionPx;

    return Math.min(desired, maxDim / Math.max(1, width), maxDim / Math.max(1, height));
  }

  private renderPdfCanvas(exportTarget: HTMLElement, scale: number, selection: PdfRowSelection, layoutViewportHeight: number): Promise<HTMLCanvasElement> {
    const width = Math.ceil(exportTarget.clientWidth || exportTarget.getBoundingClientRect().width || exportTarget.scrollWidth);
    // With the non-visible rows hidden before this runs, scrollHeight is the trimmed report height —
    // this is the output crop. windowHeight stays the full layout height so the clone renders exactly
    // as it lays out (cropping the viewport to the kept rows misaligns glyph backgrounds).
    const height = Math.ceil(exportTarget.scrollHeight || exportTarget.getBoundingClientRect().height || exportTarget.clientHeight);

    return html2canvas(exportTarget, {
      backgroundColor: PdfExportService.pageBackgroundColor,
      scale,
      useCORS: true,
      logging: false,
      width,
      height,
      windowWidth: width,
      windowHeight: layoutViewportHeight,
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: -window.scrollY,
      ignoreElements: (element: Element) => element.id === 'spinner' || selection.rowsToIgnore.has(element),
      onclone: (clonedDocument: Document) => this.applyPdfExportStyles(clonedDocument)
    });
  }

  // html2canvas 1.4.1 cannot render box-shadow or radial-gradient. Inject a
  // stylesheet into its throwaway document clone that swaps those for supported
  // equivalents, so the export matches the live app without touching real DOM/SCSS.
  private applyPdfExportStyles(clonedDocument: Document): void {
    if (clonedDocument.getElementById(PdfExportService.pdfOverrideStyleId)) {
      return;
    }

    const style = clonedDocument.createElement('style');
    style.id = PdfExportService.pdfOverrideStyleId;
    style.textContent = this.pdfExportOverrideCss();
    (clonedDocument.head ?? clonedDocument.documentElement).appendChild(style);
  }

  private pdfExportOverrideCss(): string {
    const grey = PdfExportService.pageBackgroundColor;
    return `
      html, body, .pdf-export-target {
        background-color: ${grey} !important;
      }
      .mat-mdc-card, .mat-elevation-z1 {
        border: 1px solid #e0e0e0 !important;
        box-shadow: none !important;
      }
      .bg-green, .bg-red, .bg-yellow {
        background-image: none !important;
        border-radius: 50% !important;
      }
      .bg-green { background-color: #4caf50 !important; }
      .bg-red { background-color: var(--red) !important; }
      .bg-yellow { background-color: #ffeb3b !important; }
    `;
  }

  private waitForPdfStyles(): Promise<void> {
    return new Promise(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  // Measure the canvas-pixel extents of elements we don't want a page break to slice through
  // (table rows, billboard/panel cards, charts). Positions are relative to the export target top.
  private collectBreakIntervals(exportTarget: HTMLElement, scale: number): PdfBreakInterval[] {
    const selectors = 'tr, mat-card, .mat-mdc-card, sd-chart, canvas';
    const targetTop = exportTarget.getBoundingClientRect().top;
    const intervals: PdfBreakInterval[] = [];

    exportTarget.querySelectorAll<HTMLElement>(selectors).forEach(element => {
      const rect = element.getBoundingClientRect();
      if (rect.height <= 0) {
        return;
      }
      intervals.push({
        top: (rect.top - targetTop) * scale,
        bottom: (rect.bottom - targetTop) * scale
      });
    });

    return intervals;
  }

  // Choose page-slice boundaries so cuts land between protected elements, not through them.
  // Returns slice edges starting at 0 and ending at totalHeight; no slice exceeds pageHeight.
  private computePageBreaks(totalHeight: number, pageHeight: number, intervals: PdfBreakInterval[]): number[] {
    // Elements taller than a page can't be kept whole, so they don't constrain the cut
    // (their child rows, which do fit, still constrain it).
    const protectedIntervals = intervals.filter(interval => interval.bottom - interval.top <= pageHeight);

    const breaks = [0];
    let current = 0;

    while (current < totalHeight) {
      let cut = current + pageHeight;

      if (cut >= totalHeight) {
        cut = totalHeight;
      } else {
        // If the cut falls inside a protected element, pull it up to that element's top.
        // Repeat until the cut sits in a gap (or we can't make progress).
        for (;;) {
          const straddling = protectedIntervals.filter(interval => interval.top < cut && cut < interval.bottom);
          if (straddling.length === 0) {
            break;
          }
          const highestTop = Math.min(...straddling.map(interval => interval.top));
          if (highestTop <= current) {
            // No clean break fits on this page; fall back to a hard cut to guarantee progress.
            cut = current + pageHeight;
            break;
          }
          cut = highestTop;
        }
      }

      breaks.push(cut);
      current = cut;
    }

    return breaks;
  }

  private buildPdfDocument(canvas: HTMLCanvasElement, breakIntervals: PdfBreakInterval[] = [], footerNote: string | null = null): jsPDF {
    const orientation: 'p' | 'l' = canvas.width > canvas.height ? 'l' : 'p';
    const pdf = new jsPDF({
      orientation,
      unit: 'pt',
      format: 'letter',
      compress: true
    });
    const margin = 18;
    const pageWidth = pdf.internal.pageSize.getWidth() - (margin * 2);
    const pageHeight = pdf.internal.pageSize.getHeight() - (margin * 2);
    const scale = pageWidth / canvas.width;
    const pageHeightInPixels = Math.max(1, Math.floor(pageHeight / scale));

    const breaks = this.computePageBreaks(canvas.height, pageHeightInPixels, breakIntervals);

    let lastImageBottom = margin;
    for (let page = 0; page < breaks.length - 1; page += 1) {
      const offsetY = breaks[page];
      const sliceHeight = breaks[page + 1] - offsetY;
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeight;

      const context = pageCanvas.getContext('2d');
      if (!context) {
        throw new Error('Unable to create a canvas context for PDF export.');
      }

      context.fillStyle = PdfExportService.pageBackgroundColor;
      context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      context.drawImage(
        canvas,
        0,
        offsetY,
        canvas.width,
        sliceHeight,
        0,
        0,
        pageCanvas.width,
        pageCanvas.height
      );

      if (page > 0) {
        pdf.addPage();
      }

      const imageHeight = sliceHeight * scale;
      pdf.addImage(pageCanvas.toDataURL('image/png'), 'PNG', margin, margin, pageWidth, imageHeight, undefined, 'FAST');
      lastImageBottom = margin + imageHeight;
    }

    if (footerNote) {
      const lineGap = 14;
      const pageBottom = pdf.internal.pageSize.getHeight() - margin;
      let noteY = lastImageBottom + lineGap;
      if (noteY > pageBottom) {
        pdf.addPage();
        noteY = margin + lineGap;
      }
      pdf.setFontSize(9);
      pdf.setTextColor(90);
      pdf.text(footerNote, margin, noteY);
      pdf.setTextColor(0);
    }

    return pdf;
  }

  // The note stamped under a table trimmed to its visible scroll window. Null if nothing was dropped.
  private static visibleRowsNote(shownRows: number, totalRows: number): string | null {
    if (totalRows <= shownRows) {
      return null;
    }
    return `Showing ${shownRows} of ${totalRows} rows visible on screen`;
  }

  private asError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
  }
}
